import { formatDate } from "../lib/format";

export interface CycleDay {
  date: string;
  kept: number;
  outside: number;
}

function inWindow(iso: string, start?: string | null, end?: string | null): boolean {
  if (!start || !end) return false;
  return iso >= start && iso <= end;
}

function shortDay(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function StatementCycleChart({
  days,
  periodStart,
  periodEnd,
  title,
}: {
  days: CycleDay[];
  periodStart?: string | null;
  periodEnd?: string | null;
  title?: string;
}) {
  if (!days.length) return null;
  const max = Math.max(1, ...days.map((d) => d.kept + d.outside));
  const showEveryLabel = days.length <= 16;
  const hasOutside = days.some((d) => d.outside > 0);
  const hasEdge = days.some(
    (d) => d.kept > 0 && periodStart && periodEnd && !inWindow(d.date, periodStart, periodEnd),
  );
  const keptTotal = days.reduce((sum, d) => sum + d.kept, 0);
  const outsideTotal = days.reduce((sum, d) => sum + d.outside, 0);

  function showLabel(iso: string, index: number): boolean {
    if (showEveryLabel || index === 0 || index === days.length - 1) return true;
    if (iso === periodStart || iso === periodEnd) return true;
    return iso.endsWith("-01");
  }

  return (
    <figure className="cycle-chart">
      {title && <figcaption className="cycle-chart-title">{title}</figcaption>}
      <div
        className="cycle-chart-bars"
        role="img"
        aria-label={`${keptTotal} transactions in view, ${outsideTotal} outside the statement`}
      >
        {days.map((day, index) => {
          const total = day.kept + day.outside;
          const inside = inWindow(day.date, periodStart, periodEnd);
          const edge = day.kept > 0 && !!periodStart && !!periodEnd && !inside;
          return (
            <div
              key={day.date}
              className={`cycle-col${inside ? " in-window" : ""}`}
              title={`${formatDate(day.date)}: ${day.kept} on statement${
                day.outside ? `, ${day.outside} outside` : ""
              }`}
            >
              <div className="cycle-plot">
                <div className="cycle-stack" style={{ height: `${(total / max) * 100}%` }}>
                  {day.outside > 0 && (
                    <span className="cycle-outside" style={{ flex: day.outside }} />
                  )}
                  {day.kept > 0 && (
                    <span
                      className={`cycle-kept${edge ? " edge" : ""}`}
                      style={{ flex: day.kept }}
                    />
                  )}
                </div>
              </div>
              <span className="cycle-tick">{showLabel(day.date, index) ? shortDay(day.date) : ""}</span>
            </div>
          );
        })}
      </div>
      <div className="cycle-legend">
        <span>
          <i className="swatch kept" /> On the statement
        </span>
        {hasEdge && (
          <span>
            <i className="swatch edge" /> Dated outside, still included
          </span>
        )}
        {hasOutside && (
          <span>
            <i className="swatch outside" /> Not on this statement
          </span>
        )}
        {periodStart && periodEnd && (
          <span className="muted">
            Shaded band {formatDate(periodStart)} → {formatDate(periodEnd)}
          </span>
        )}
      </div>
    </figure>
  );
}
